// src/features/brain/BrainMessage.tsx
// Phase 6.6 Bundle 3.2 - Single message bubble.
// Bundle 6.7.5 - isPartial indicator for incomplete streamed responses.
//
// Renders one message from a Brain conversation. User messages right-aligned
// with maple accent; assistant messages left-aligned with card surface.
// Assistant messages with citations show a Sources accordion below the text,
// each citation linking to the entity's detail page.
//
// Citation entity_type values map to /app/<entity>s/<entity_id>.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, ExternalLink, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BrainCitation } from "./useBrainQueries";

interface BrainMessageProps {
  role: "user" | "assistant";
  content: string;
  citations?: BrainCitation[] | null;
  // If true, this is an in-progress streamed assistant message. Shows a
  // blinking caret at the end of the content.
  isStreaming?: boolean;
  // 6.7.5: if true, this assistant message is an incomplete response
  // persisted after an abnormal stream end. Shows a partial label.
  isPartial?: boolean;
}

const ENTITY_TYPE_TO_ROUTE: Record<string, string> = {
  property: "/app/properties",
  unit: "/app/units",
  tenant: "/app/tenants",
  lease: "/app/leases",
};

// Phase 6.8: the server streams raw deltas that include [SOURCE:...] markers
// (stripped only at persistence time), so live-streamed and client-persisted
// partial content can contain them. Strip complete markers and a trailing
// unterminated one from anything we render.
function stripMarkersForDisplay(text: string): string {
  return text
    .replace(/\[SOURCE:[^\]]*\]/g, "")
    .replace(/\[SOURCE:[^\]]*$/, "");
}

export function BrainMessage({
  role,
  content,
  citations,
  isStreaming,
  isPartial,
}: BrainMessageProps) {
  const { t } = useTranslation();
  const [sourcesOpen, setSourcesOpen] = useState(false);

  const isUser = role === "user";
  const hasCitations = !!citations && citations.length > 0;

  return (
    <div
      className={cn(
        "flex w-full mb-4 animate-fade-in",
        isUser ? "justify-end" : "justify-start",
      )}
    >
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-4 py-2.5 text-sm",
          isUser
            ? "bg-maple-600 text-white"
            : "bg-card text-card-foreground border border-border",
          !isUser && isPartial && "border-amber-500/40",
        )}
      >
        <div className="whitespace-pre-wrap break-words leading-relaxed">
          {isUser ? content : stripMarkersForDisplay(content)}
          {isStreaming && (
            <span
              className="inline-block w-1.5 h-4 bg-current ml-0.5 align-middle animate-pulse"
              aria-hidden="true"
            />
          )}
        </div>

        {!isUser && isPartial && !isStreaming && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t("brain.partialLabel")}
          </div>
        )}

        {!isUser && hasCitations && (
          <div className="mt-3 pt-3 border-t border-border">
            <button
              type="button"
              onClick={() => setSourcesOpen(!sourcesOpen)}
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors min-h-[44px] -my-2 py-2"
              aria-expanded={sourcesOpen}
            >
              {sourcesOpen ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {t("brain.sources")} ({citations.length})
            </button>

            {sourcesOpen && (
              <ul className="mt-2 space-y-1.5">
                {citations.map((cite, idx) => {
                  const route = ENTITY_TYPE_TO_ROUTE[cite.entity_type];
                  const href = route
                    ? `${route}/${cite.entity_id}`
                    : null;

                  if (!href) {
                    return (
                      <li
                        key={idx}
                        className="text-xs text-muted-foreground px-2 py-1.5"
                      >
                        {cite.label}
                      </li>
                    );
                  }

                  return (
                    <li key={idx}>
                      <Link
                        to={href}
                        className="flex items-center gap-1.5 text-xs text-foreground hover:text-maple-600 transition-colors px-2 py-1.5 rounded hover:bg-accent min-h-[44px] -my-1"
                      >
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{cite.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
